import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { FamilyRelationModal } from '../components/FamilyRelationModal';
import { familyRelationsApi } from '../familyRelationsApi';
import type { FamilyRelation } from '../familyRelationTypes';

/**
 * The family-relation catalog — what the personnel profile's Family panel is built from.
 *
 * The anchor opens the **profile**, not the edit form: every index page in this product is
 * partnered with one, and here the profile is where the count of members recorded at a relation
 * is worth reading before deciding anything about the row.
 */
export function FamilyRelationsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<FamilyRelation | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    relation: FamilyRelation;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRelations = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      familyRelationsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (relation: FamilyRelation): MenuItem[] => [
      {
        key: `edit-${relation.id}`,
        label: 'Edit',
        permission: 'family-relations.update',
        onClick: () => setEditing(relation),
      },
      {
        key: `restore-${relation.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'family-relations.delete',
        disabledReason: relation.isActive ? 'This relation is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', relation }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await familyRelationsApi.deactivate(confirming.relation.id);
      } else {
        await familyRelationsApi.activate(confirming.relation.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<FamilyRelation>
        module="family-relations"
        // Reached through Personnel → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Personnel',
          { label: 'Settings', to: '/personnel/settings' },
          'Family Relations',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['FamilyRelation']}
        rowKey={(relation) => relation.id}
        fetch={fetchRelations}
        reloadKey={reloadKey}
        searchPlaceholder="Search relations"
        emptyMessage="No relations yet. Add the ones this employer records — CS Form 212 asks for a spouse, both parents and the children."
        onCreate={() => setCreating(true)}
        createPermission="family-relations.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(relation) => navigate(`/personnel/settings/family-relations/${relation.id}`)}
        openPermission="family-relations.read"
        rowActions={rowActions}
        onDelete={(relation) => setConfirming({ kind: 'deactivate', relation })}
        deletePermission="family-relations.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(relation) =>
          relation.isActive ? null : 'This relation is already deleted.'
        }
        onClearData={() => familyRelationsApi.clear()}
        clearDataPermission="family-relations.clear-data"
        otherInfo={(relation) => relation}
      />

      {creating || editing !== null ? (
        <FamilyRelationModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete relation' : 'Restore relation'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`family-relation-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.relation.name}" stops being offered on the family form.`
                  : `"${confirming.relation.name}" is offered again on the family form.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.relation.recordCount > 0 ? (
                <p>
                  {confirming.relation.recordCount} family member
                  {confirming.relation.recordCount === 1 ? ' is' : 's are'} recorded at this
                  relation. Those rows stay on their records and still name it — removing a
                  choice does not erase what was collected.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted relation can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<FamilyRelation>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 200,
    flex: true,
    sortable: true,
    render: (relation) => <TextCell value={relation.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 300,
    summary: true,
    render: (relation) => <TextCell value={relation.description} />,
  },
  {
    /*
      The three flags read down as a column of Yes/— rather than as a sentence per row: this
      list is scanned to compare relations against each other, which is what a grid is for.
      Em dash for "no", per the absent-value rule — a blank cell reads as a broken layout.
    */
    key: 'allowsMultiple',
    header: 'Several',
    width: 100,
    summary: true,
    render: (relation) => <TextCell value={relation.allowsMultiple ? 'Yes' : null} />,
  },
  {
    key: 'requiresDateOfBirth',
    header: 'Birth date',
    width: 110,
    render: (relation) => <TextCell value={relation.requiresDateOfBirth ? 'Required' : null} />,
  },
  {
    key: 'collectsOccupation',
    header: 'Occupation',
    width: 110,
    render: (relation) => <TextCell value={relation.collectsOccupation ? 'Asked' : null} />,
  },
  {
    // The stored sort value, under a heading that says so: a POSITION is only correct with the
    // whole ladder in hand, and a paged table never has it.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (relation) => <TextCell value={String(relation.sortOrder)} mono numeric />,
  },
  {
    key: 'recordCount',
    header: 'Members',
    width: 100,
    align: 'right',
    summary: true,
    // The count is of personnel records, which is another module's data — the name and flags
    // beside it are this module's own and stay ungated.
    permission: 'personnel.read',
    render: (relation) => <TextCell value={String(relation.recordCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (relation) =>
      relation.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
